import { create } from 'zustand';
import { employeeService } from '../services/employeeService';
import {
  CreateEmployeeRequest,
  Employee,
  EmployeeStatus,
  EmployeesQueryParams,
} from '../types/employee';
import { ApiError } from '../types/api';

interface EmployeeState {
  employees: Employee[];
  currentPage: number;
  pageSize: number;
  total: number;
  totalPages: number;
  searchQuery: string;
  roleFilter: string;
  branchFilter: string;
  statusFilter: string;
  isLoading: boolean;
  statusUpdatingEmployeeId: number | null;
  invitingEmployeeId: number | null;
  error: ApiError | null;

  fetchEmployees: () => Promise<void>;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSearchQuery: (query: string) => void;
  setRoleFilter: (role: string) => void;
  setBranchFilter: (branchId: string) => void;
  setStatusFilter: (status: string) => void;
  searchEmployees: (query: string) => Promise<void>;
  clearFilters: () => void;
  createEmployee: (data: CreateEmployeeRequest) => Promise<void>;
  toggleEmployeeStatus: (id: number, currentStatus: EmployeeStatus) => Promise<void>;
  sendInviteEmail: (id: number) => Promise<void>;
  clearError: () => void;
}

const buildQueryParams = (state: EmployeeState): EmployeesQueryParams => ({
  page: state.currentPage,
  limit: state.pageSize,
  search: state.searchQuery || undefined,
  role: state.roleFilter,
  branchId:
    state.branchFilter !== 'all' ? Number(state.branchFilter) : undefined,
  status: state.statusFilter,
});

export const useEmployeeStore = create<EmployeeState>()((set, get) => ({
  employees: [],
  currentPage: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
  searchQuery: '',
  roleFilter: 'all',
  branchFilter: 'all',
  statusFilter: 'all',
  isLoading: false,
  statusUpdatingEmployeeId: null,
  invitingEmployeeId: null,
  error: null,

  fetchEmployees: async () => {
    const state = get();
    set({ isLoading: true, error: null });
    try {
      const response = await employeeService.getEmployees(
        buildQueryParams(state)
      );
      set({
        employees: response.data,
        currentPage: response.metaData.page,
        pageSize: response.metaData.limit,
        total: response.metaData.total,
        totalPages: response.metaData.totalPages,
        isLoading: false,
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  setPage: (page: number) => {
    set({ currentPage: page });
    get().fetchEmployees();
  },

  setPageSize: (size: number) => {
    set({ pageSize: size, currentPage: 1 });
    get().fetchEmployees();
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  setRoleFilter: (role: string) => {
    set({ roleFilter: role, currentPage: 1 });
    get().fetchEmployees();
  },

  setBranchFilter: (branchId: string) => {
    set({ branchFilter: branchId, currentPage: 1 });
    get().fetchEmployees();
  },

  setStatusFilter: (status: string) => {
    set({ statusFilter: status, currentPage: 1 });
    get().fetchEmployees();
  },

  searchEmployees: async (query: string) => {
    set({ searchQuery: query, currentPage: 1 });
    await get().fetchEmployees();
  },

  clearFilters: () => {
    set({
      searchQuery: '',
      roleFilter: 'all',
      branchFilter: 'all',
      statusFilter: 'all',
      currentPage: 1,
    });
    get().fetchEmployees();
  },

  createEmployee: async (data: CreateEmployeeRequest) => {
    set({ isLoading: true, error: null });
    try {
      await employeeService.createEmployee(data);
      await get().fetchEmployees();
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  toggleEmployeeStatus: async (id: number, currentStatus: EmployeeStatus) => {
    set({ statusUpdatingEmployeeId: id, error: null });
    try {
      if (currentStatus === 'active') {
        await employeeService.deactivateEmployee(id);
      } else {
        await employeeService.activateEmployee(id);
      }
      await get().fetchEmployees();
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    } finally {
      set({ statusUpdatingEmployeeId: null });
    }
  },

  sendInviteEmail: async (id: number) => {
    set({ invitingEmployeeId: id, error: null });
    try {
      await employeeService.sendInvite(id);
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    } finally {
      set({ invitingEmployeeId: null });
    }
  },

  clearError: () => set({ error: null }),
}));
